import { useEffect, useRef } from "react";
import type { ExecutiveData } from "@/hooks/useDashboardData";

interface Props {
  data: ExecutiveData;
}

function bandClass(score: number | null): string {
  if (score === null) return "lq-heat-empty";
  if (score >= 7) return "lq-heat-strong";
  if (score >= 5.5) return "lq-heat-dev";
  if (score >= 4.5) return "lq-heat-low";
  return "lq-heat-risk";
}

function initials(name: string): string {
  const parts = name.trim().split(/\s+/);
  if (parts.length === 0) return "??";
  if (parts.length === 1) return (parts[0]?.charAt(0) ?? "?").toUpperCase();
  return ((parts[0]?.charAt(0) ?? "") + (parts[parts.length - 1]?.charAt(0) ?? "")).toUpperCase();
}

interface Theme { title: string; body: string; severity: "e" | "a" | "r" }

function extractThemes(insights: unknown): Theme[] {
  // The existing cohort_insights.insights is { strengths, development, themes, recommendations, readiness, ... }
  // Convert into 3 cards for the ribbon.
  if (insights === null || typeof insights !== "object") return [];
  const i = insights as Record<string, unknown>;
  const cards: Theme[] = [];
  const strengths = typeof i["strengths"] === "string" ? (i["strengths"] as string) : "";
  const development = typeof i["development"] === "string" ? (i["development"] as string) : "";
  const themes = typeof i["themes"] === "string" ? (i["themes"] as string) : "";
  if (development !== "") cards.push({ title: "Development gaps", body: development.split(".")[0] + ".", severity: "r" });
  if (themes !== "")      cards.push({ title: "Cross-cohort themes", body: themes.split(".")[0] + ".", severity: "a" });
  if (strengths !== "")   cards.push({ title: "What's landing", body: strengths.split(".")[0] + ".", severity: "e" });
  return cards.slice(0, 3);
}

export function DashboardExecutiveView({ data }: Props): JSX.Element {
  const tipRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const tip = tipRef.current;
    if (tip === null) return;
    const elements = document.querySelectorAll<HTMLElement>(".lq-root [data-tip]");
    const show = (e: Event): void => {
      const el = e.currentTarget as HTMLElement;
      tip.textContent = el.dataset["tip"] ?? "";
      tip.classList.add("show");
    };
    const move = (e: MouseEvent): void => {
      tip.style.left = `${e.clientX + 14}px`;
      tip.style.top = `${e.clientY + 14}px`;
    };
    const hide = (): void => tip.classList.remove("show");
    elements.forEach((el) => {
      el.addEventListener("mouseenter", show);
      el.addEventListener("mousemove", move as unknown as EventListener);
      el.addEventListener("mouseleave", hide);
    });
    return () => {
      elements.forEach((el) => {
        el.removeEventListener("mouseenter", show);
        el.removeEventListener("mousemove", move as unknown as EventListener);
        el.removeEventListener("mouseleave", hide);
      });
    };
  }, [data]);

  const total = data.readiness.total;
  const strongPct = total > 0 ? Math.round((data.readiness.strong / total) * 100) : 0;
  const developingPct = total > 0 ? Math.round((data.readiness.developing / total) * 100) : 0;
  const needsPct = total > 0 ? Math.round((data.readiness.needsWork / total) * 100) : 0;
  const readyPct = total > 0 ? Math.round((data.readiness.strong / total) * 100) : 0;

  const themes = data.themes !== null ? extractThemes(data.themes.insights) : [];

  return (
    <>
      <div className="lq-glass" style={{ padding: "20px 22px", marginBottom: 12 }}>
        <div style={{ display: "grid", gridTemplateColumns: "1.3fr 1px 1fr", gap: 22, alignItems: "center" }}>
          <div>
            <div className="lq-eyebrow" style={{ marginBottom: 12 }}>READINESS</div>
            <div style={{ display: "flex", alignItems: "baseline", gap: 10, marginBottom: 18 }}>
              <div className="lq-stat-num">{readyPct}</div>
              <div className="lq-stat-sub">% ready</div>
              <div style={{ marginLeft: "auto", fontSize: 11, color: "#71717A" }}>{data.readiness.strong} of {total}</div>
            </div>
            {total > 0 ? (
              <div style={{ display: "flex", height: 8, borderRadius: 4, overflow: "hidden", background: "rgba(255,255,255,0.04)" }}>
                <div style={{ width: `${strongPct}%`, background: "linear-gradient(to right, #10B981, #34D399)", boxShadow: "0 0 12px rgba(52,211,153,0.3)" }} data-tip={`Strong · ${data.readiness.strong} leaders · ${strongPct}%`} />
                <div style={{ width: `${developingPct}%`, background: "linear-gradient(to right, #F59E0B, #FBBF24)", boxShadow: "0 0 12px rgba(251,191,36,0.25)" }} data-tip={`Developing · ${data.readiness.developing} leaders · ${developingPct}%`} />
                <div style={{ width: `${needsPct}%`, background: "linear-gradient(to right, #EF4444, #F87171)", boxShadow: "0 0 12px rgba(248,113,113,0.25)" }} data-tip={`Needs work · ${data.readiness.needsWork} leaders · ${needsPct}%`} />
              </div>
            ) : (
              <div style={{ fontSize: 12, color: "#71717A" }}>No completed attempts in this period.</div>
            )}
            <div style={{ display: "flex", gap: 16, marginTop: 10, fontSize: 11 }}>
              <span style={{ display: "flex", alignItems: "center", gap: 6, color: "#A1A1AA" }}><span style={{ width: 8, height: 8, borderRadius: "50%", background: "#34D399", boxShadow: "0 0 6px rgba(52,211,153,0.5)" }} />Strong <strong style={{ color: "#FAFAFA", fontWeight: 500 }}>{data.readiness.strong}</strong></span>
              <span style={{ display: "flex", alignItems: "center", gap: 6, color: "#A1A1AA" }}><span style={{ width: 8, height: 8, borderRadius: "50%", background: "#FBBF24", boxShadow: "0 0 6px rgba(251,191,36,0.4)" }} />Developing <strong style={{ color: "#FAFAFA", fontWeight: 500 }}>{data.readiness.developing}</strong></span>
              <span style={{ display: "flex", alignItems: "center", gap: 6, color: "#A1A1AA" }}><span style={{ width: 8, height: 8, borderRadius: "50%", background: "#F87171", boxShadow: "0 0 6px rgba(248,113,113,0.4)" }} />Needs work <strong style={{ color: "#FAFAFA", fontWeight: 500 }}>{data.readiness.needsWork}</strong></span>
            </div>
          </div>
          <div style={{ width: 1, alignSelf: "stretch", background: "linear-gradient(to bottom, transparent, rgba(255,255,255,0.1), transparent)" }} />
          <div>
            <div className="lq-eyebrow" style={{ marginBottom: 12 }}>AVG UPLIFT · ATTEMPT 1 → LATEST</div>
            <div style={{ display: "flex", alignItems: "baseline", gap: 6, marginBottom: 8 }}>
              <div className="lq-stat-num" style={{ color: data.uplift.delta !== null && data.uplift.delta >= 0 ? "#34D399" : "#F87171" }}>
                {data.uplift.delta === null ? "—" : `${data.uplift.delta >= 0 ? "+" : ""}${data.uplift.delta.toFixed(1)}`}
              </div>
              <div className="lq-stat-sub">points / 10</div>
            </div>
            <div style={{ fontSize: 12, color: "#A1A1AA", lineHeight: 1.5 }}>
              {data.uplift.n === 0 ? (
                "No learner has retaken a sim yet."
              ) : (
                <>
                  {data.uplift.n} learner{data.uplift.n === 1 ? "" : "s"} retook a sim. Score moved{" "}
                  <span style={{ color: "#FAFAFA", fontWeight: 500, fontVariantNumeric: "tabular-nums" }}>
                    {data.uplift.firstScore?.toFixed(1)} → {data.uplift.latestScore?.toFixed(1)}
                  </span>.
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {themes.length > 0 && (
        <div className="lq-glass lq-glass-v" style={{ padding: "16px 18px", marginBottom: 12 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
            <span style={{ fontSize: 11, color: "#A78BFA", letterSpacing: 0.8 }}>✦ CROSS-COHORT THEMES · CLAUDE</span>
            {data.themes !== null && (
              <span style={{ fontSize: 11, color: "#71717A", marginLeft: "auto" }}>
                {new Date(data.themes.generatedAt).toLocaleString("en-GB", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}
              </span>
            )}
          </div>
          <div style={{ display: "grid", gridTemplateColumns: `repeat(${themes.length}, 1fr)`, gap: 10 }}>
            {themes.map((t, i) => (
              <div key={i} className="lq-theme">
                <div className={`lq-theme-rule lq-rule-${t.severity}`} />
                <div>
                  <div style={{ fontSize: 12, fontWeight: 500, lineHeight: 1.3, color: "#FAFAFA" }}>{t.title}</div>
                  <div style={{ fontSize: 11, color: "#A1A1AA", marginTop: 4, lineHeight: 1.5 }}>{t.body}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 12 }}>
        <div className="lq-glass" style={{ padding: "16px 18px" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 10 }}>
            <div style={{ fontSize: 13, fontWeight: 500 }}>Competency profile</div>
            <div style={{ fontSize: 10, color: "#71717A" }}>avg / 10</div>
          </div>
          {data.competencies.length === 0 ? (
            <div style={{ fontSize: 12, color: "#71717A", padding: "20px 0" }}>No completed attempts in this period.</div>
          ) : (
            data.competencies.map((c) => (
              <div key={c.name} style={{ display: "grid", gridTemplateColumns: "1fr 1fr 60px", gap: 10, alignItems: "center", padding: "7px 0" }}>
                <div style={{ fontSize: 12, color: "#D4D4D8" }}>{c.name}<span style={{ color: "#52525B", marginLeft: 6 }}>n={c.n}</span></div>
                <div className="lq-track">
                  <div className={`lq-track-fill lq-track-fill-${c.avgScore !== null && c.avgScore >= 7 ? "e" : c.avgScore !== null && c.avgScore >= 5 ? "a" : "r"}`}
                       style={{ width: c.avgScore !== null ? `${(c.avgScore / 10) * 100}%` : "0%" }} />
                </div>
                <div style={{ fontSize: 13, fontWeight: 500, textAlign: "right", fontVariantNumeric: "tabular-nums" }}>{c.avgScore !== null ? c.avgScore.toFixed(1) : "—"}</div>
              </div>
            ))
          )}
        </div>

        <div className="lq-glass" style={{ padding: "16px 18px" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 12 }}>
            <div style={{ fontSize: 13, fontWeight: 500 }}>Cohort highlights</div>
          </div>
          {data.standoutCohort === null && data.concernCohort === null && (
            <div style={{ fontSize: 12, color: "#71717A" }}>Need at least one cohort with 3+ completions to surface highlights.</div>
          )}
          {data.standoutCohort !== null && (
            <div style={{ marginBottom: 12, padding: 12, borderRadius: 10, background: "rgba(16,185,129,0.06)", border: "1px solid rgba(16,185,129,0.22)" }}>
              <span className="lq-pill lq-pill-e" style={{ marginBottom: 6 }}>STANDOUT</span>
              <div style={{ fontSize: 14, fontWeight: 500, marginTop: 4 }}>{data.standoutCohort.cohortName}</div>
              <div style={{ fontSize: 11, color: "#A1A1AA", marginTop: 4 }}>n={data.standoutCohort.n} · avg {data.standoutCohort.avgScore?.toFixed(1)} / 10</div>
            </div>
          )}
          {data.concernCohort !== null && data.concernCohort.cohortName !== data.standoutCohort?.cohortName && (
            <div style={{ padding: 12, borderRadius: 10, background: "rgba(239,68,68,0.06)", border: "1px solid rgba(239,68,68,0.22)" }}>
              <span className="lq-pill lq-pill-r" style={{ marginBottom: 6 }}>CONCERN</span>
              <div style={{ fontSize: 14, fontWeight: 500, marginTop: 4 }}>{data.concernCohort.cohortName}</div>
              <div style={{ fontSize: 11, color: "#A1A1AA", marginTop: 4 }}>n={data.concernCohort.n} · avg {data.concernCohort.avgScore?.toFixed(1)} / 10</div>
            </div>
          )}
        </div>
      </div>

      <div className="lq-glass" style={{ padding: "16px 18px" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 10 }}>
          <div style={{ fontSize: 13, fontWeight: 500 }}>Top 10 performers</div>
          <div style={{ fontSize: 10, color: "#71717A" }}>by avg score across completed attempts</div>
        </div>
        {data.topPerformers.length === 0 ? (
          <div style={{ fontSize: 12, color: "#71717A", padding: "20px 0" }}>No completed attempts yet.</div>
        ) : (
          data.topPerformers.map((p, idx) => (
            <div key={p.userId} className="lq-perf-row">
              <span className="lq-rank">{String(idx + 1).padStart(2, "0")}</span>
              <div className={`lq-avatar${p.avgScore !== null && p.avgScore < 5 ? " lq-avatar-r" : ""}`}>{initials(p.fullName)}</div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 12 }}>{p.fullName}</div>
                <div style={{ fontSize: 10, color: "#71717A" }}>{p.cohortName} · {p.n} sim{p.n === 1 ? "" : "s"}</div>
              </div>
              <div style={{ height: 4, borderRadius: 2, background: "rgba(255,255,255,0.05)", overflow: "hidden", maxWidth: 80, flex: 1 }}>
                <div className={`lq-track-fill lq-track-fill-${p.avgScore !== null && p.avgScore >= 7 ? "e" : p.avgScore !== null && p.avgScore >= 5 ? "a" : "r"}`}
                     style={{ width: p.avgScore !== null ? `${(p.avgScore / 10) * 100}%` : "0%", height: "100%" }} />
              </div>
              <div style={{ fontSize: 13, fontWeight: 500, width: 40, textAlign: "right", fontVariantNumeric: "tabular-nums" }}>
                {p.avgScore !== null ? p.avgScore.toFixed(1) : "—"}
              </div>
            </div>
          ))
        )}
        {/* eslint-disable-next-line react/no-unknown-property */}
        <div ref={tipRef} className="lq-tip" />
        {/* swap heatmap stub for future expansion */}
        <span className="lq-heat-cell lq-heat-empty" style={{ display: "none" }} aria-hidden="true">_</span>
        {/* keep references so band classes are emitted */}
        <span className={bandClass(0)} style={{ display: "none" }} aria-hidden="true">_</span>
      </div>
    </>
  );
}
